// @flow
import React from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {useForm} from 'react-hook-form';
import {Redirect, useLocation} from 'react-router-dom';
import ForgotPassword from './ForgotPassword';

import type {User, Store} from '../../../entities';
import {usePage} from '../../page/utils';
import analytics from '../../../analytics';
import {useState} from 'react';

type Props = {|
  login: ({email: string, password: string}) => void,
  isAuthenticating?: boolean,
  loginError?: string,
  user?: User,
  store?: Store,
  sendEmailRecovery: ({emailRecovery: string}) => void,
  resetPassword: ({token: string, password: string}) => void,
  isResettingPassword?: boolean,
  resetPasswordSuccess?: boolean,
  resetPasswordError?: string,
|};

const useStyles = makeStyles(theme => ({
  logo: {
    height: 48,
    width: 'auto',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 64px)',
    position: 'relative',
  },
  wrapperInput: {
    position: 'relative',
  },
  visibilityButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: '25%',
    transform: ' translate(0, -25%)',
  },
  formWrap: {
    backgroundColor: '#E7F1FD',
    boxSizing: 'border-box',
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: theme.borderRadius,
    width: '100%',
    maxWidth: 440,
  },
  form: {
    width: '100%',
  },
  inputForm: {
    marginBottom: theme.spacing(3),
  },
  innerInputForm: {
    background: 'white',
  },
  bigTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 600,
  },
  submit: {
    margin: theme.spacing(1, 0, 2),
    fontWeight: 600,
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  loading: {
    position: 'absolute',
  },
  linkText: {
    fontSize: 14,
    textDecoration: 'underline',
  },
  linkBack: {
    fontSize: 14,
    textDecoration: 'underline',
    color: '#615E6E',
  },
  navBackground: {
    backgroundColor: 'white!important',
  },
}));

export default usePage<Props>(Auth, 'Login');

function Auth(props: Props) {
  const classes = useStyles();
  const {
    isAuthenticating,
    loginError,
    user,
    store,
    sendEmailRecovery,
    resetPassword,
    isResettingPassword,
    resetPasswordSuccess,
    resetPasswordError,
  } = props;
  const {handleSubmit, register, errors} = useForm({
    defaultValues: {
      email: process.env.REACT_APP_DEFAULT_ADMIN_EMAIL || '',
      password: process.env.REACT_APP_DEFAULT_ADMIN_PASSWORD || '',
    },
  });

  const [visibility, setVisibility] = useState(false);

  const location = useLocation();

  const handleToggleVisibility = () => {
    setVisibility(!visibility);
  };
  const onSubmit = values => {
    setVisibility(false);
    const {login} = props;
    login({
      email: values.email,
      password: values.password,
    });
  };

  if (user && store) {
    analytics.fireCustomEvent('User Logged In', undefined, ['user-guiding']);
    if (!store.initialConfig) {
      return (
        <Redirect
          to={{pathname: '/on-boarding', search: window.location.search}}
        />
      );
    } else {
      return (
        <Redirect
          to={{
            pathname:
              location && location.state && location.state.referrer
                ? location.state.referrer
                : '/home',
            search: window.location.search,
          }}
        />
      );
    }
  }

  return (
    <Container maxWidth="sm">
      <div className={classes.paper}>
        <Typography component="h1" variant="h3" className={classes.bigTitle}>
          ¡Bienvenido!
        </Typography>
        <div className={classes.formWrap}>
          <form
            className={classes.form}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <TextField
              className={classes.inputForm}
              variant="outlined"
              required
              fullWidth
              id="email"
              label="Correo electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Formato de email inválido',
                },
              })}
              inputProps={{className: classes.innerInputForm}}
              disabled={isAuthenticating}
            />
            <div className={classes.wrapperInput}>
              <TextField
                className={classes.inputForm}
                variant="outlined"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={visibility ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password ? errors.password.message : ''}
                inputRef={register({required: 'Campo obligatorio'})}
                inputProps={{className: classes.innerInputForm}}
                disabled={isAuthenticating}
              />
              <IconButton
                className={classes.visibilityButton}
                size="small"
                color="primary"
                onClick={handleToggleVisibility}
              >
                {visibility ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </div>
            {loginError && (
              <Typography
                component="p"
                variant="body1"
                className={classes.errorText}
                color="error"
              >
                {loginError}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              className={classes.submit}
              disabled={isAuthenticating}
            >
              Iniciar sesión
            </Button>
          </form>
          <ForgotPassword
            resetPassword={resetPassword}
            sendEmailRecovery={sendEmailRecovery}
            isResettingPassword={isResettingPassword}
            resetPasswordSuccess={resetPasswordSuccess}
            resetPasswordError={resetPasswordError}
          />

          {/* <Link
              className={classes.linkText}
              rel="noreferrer noopener"
              href="#"
              onClick={e => handleForgotPassword(e)}
            >
              Olvidé mi contraseña
            </Link> 
            <Typography component="p" className={classes.linkText}>
            ¿No tienes una cuenta? Crea una cuenta&nbsp;
            <Link className={classes.linkInsideText} to="/">
              AQUÍ
            </Link>
          </Typography>*/}
        </div>
        {isAuthenticating && <CircularProgress className={classes.loading} />}
      </div>
    </Container>
  );
}

Auth.propTypes = {
  login: PropTypes.func.isRequired,
  isAuthenticating: PropTypes.bool,
  loginError: PropTypes.string,
  user: PropTypes.object,
  store: PropTypes.object,
  sendEmailRecovery: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  isResettingPassword: PropTypes.bool,
  resetPasswordSuccess: PropTypes.bool,
  resetPasswordError: PropTypes.string,
};
